import { isLiveNetworkSource } from '@/lib/live-network'
import type { Source, SourceType } from '@/api/types'

/**
 * Source types that are scanned when a file arrives, never on a timer.
 *
 * The server's watcher skips these outright — see the switch in
 * internal/source/watcher/watcher.go, which returns before it ever reads
 * scan_frequency. A frequency stored on one of them is dead config: it is
 * saved, displayed, and never acted on.
 *
 * `manual` is in the same position for a different reason: there is nothing to
 * scan, the assets are entered by hand.
 */
const UPLOAD_DRIVEN_TYPES: SourceType[] = [
  'source-upload',
  'binary-upload',
  'network-upload',
  'manual',
]

export function isUploadDrivenType(sourceType: SourceType): boolean {
  return UPLOAD_DRIVEN_TYPES.includes(sourceType)
}

/**
 * Whether the server will ever schedule this source on its own.
 *
 * Covers the upload types plus the live network monitor, which is fed by its
 * agent rather than probed from the server — the watcher skips that one before
 * reading scan_frequency too.
 */
export function isScheduledSource(
  source: Pick<Source, 'source_type' | 'config'>,
): boolean {
  return !isUploadDrivenType(source.source_type) && !isLiveNetworkSource(source)
}

/** What to show in place of a frequency for a source that is never scheduled. */
export const UNSCHEDULED_FREQUENCY_LABEL = 'On upload'

/**
 * The frequency to display for a source, or the standing label when the server
 * never schedules it. An empty frequency on a scheduled source means the same
 * thing by a different route: nothing auto-triggers it.
 */
export function describeScanFrequency(
  source: Pick<Source, 'source_type' | 'config' | 'scan_frequency'>,
): string {
  if (!isScheduledSource(source)) {
    return source.source_type === 'manual' ? 'Manual entry' : UNSCHEDULED_FREQUENCY_LABEL
  }
  return source.scan_frequency || 'On demand'
}
